import { Fragment, useState, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  PhotoIcon,
  ArrowTopRightOnSquareIcon,
  CameraIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { issueAPI } from '../services/api';
import { parseUserAgent } from '../utils/uaParser';
import { APP_VERSION, APP_GIT_SHA } from '../version';
import { captureScreen } from '../utils/issue-reporter/captureScreen';
import { annotateImage } from '../utils/issue-reporter/annotateImage';
import { getDiagnosticsSnapshot } from '../utils/diagnostics/diagnosticsBuffer';
import { getBreadcrumbsSnapshot } from '../utils/diagnostics/breadcrumbBuffer';

const TYPES = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature request' },
  { value: 'question', label: 'Question' },
];

const SEVERITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

function blobToFile(blob, name) {
  const ext = blob.type === 'image/jpeg' ? 'jpg' : blob.type === 'image/webp' ? 'webp' : 'png';
  return new File([blob], `${name}.${ext}`, { type: blob.type });
}

function buildContext(authState) {
  const { summary } = parseUserAgent();
  return {
    url: window.location.href,
    route: `${window.location.pathname}${window.location.search}`,
    browser: summary,
    appVersion: APP_VERSION,
    gitSha: APP_GIT_SHA,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    role: authState.user?.role_name || authState.user?.role || 'user',
    impersonating: Boolean(authState.isImpersonating),
  };
}

function IssueReporterModal({ isOpen, onClose }) {
  const auth = useAuth();
  const fileInputRef = useRef(null);

  const [type, setType] = useState('bug');
  const [severity, setSeverity] = useState('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [createdUrl, setCreatedUrl] = useState(null);

  const replaceFile = (next) => {
    if (thumbnail) URL.revokeObjectURL(thumbnail);
    if (next) {
      setFile(next);
      setThumbnail(URL.createObjectURL(next));
    } else {
      setFile(null);
      setThumbnail(null);
    }
  };

  const reset = () => {
    setType('bug');
    setSeverity('medium');
    setTitle('');
    setDescription('');
    replaceFile(null);
    setIncludeDiagnostics(true);
    setSubmitting(false);
    setCapturing(false);
    setCreatedUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (submitting || capturing) return;
    reset();
    onClose();
  };

  const onFileChange = async (e) => {
    const next = e.target.files?.[0];
    e.target.value = '';
    if (!next) return;
    if (!ALLOWED_TYPES.includes(next.type)) {
      toast.error('Screenshot must be PNG, JPEG, or WebP.');
      return;
    }
    if (next.size > MAX_BYTES) {
      toast.error('Screenshot must be 5 MB or smaller.');
      return;
    }
    const annotated = await annotateImage(next);
    replaceFile(annotated ? blobToFile(annotated, 'screenshot') : next);
  };

  const onCaptureScreen = async () => {
    if (capturing || submitting) return;
    setCapturing(true);
    try {
      const blob = await captureScreen();
      if (blob.size > MAX_BYTES) {
        toast.error('Captured screenshot is too large to attach.');
        return;
      }
      const annotated = await annotateImage(blob);
      const finalBlob = annotated || blob;
      replaceFile(blobToFile(finalBlob, 'capture'));
    } catch (err) {
      toast.error('Could not capture this page automatically. Try uploading manually.');
    } finally {
      setCapturing(false);
    }
  };

  const onEditScreenshot = async () => {
    if (!file || capturing || submitting) return;
    const annotated = await annotateImage(file);
    if (annotated) replaceFile(blobToFile(annotated, 'screenshot'));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!title.trim() || !description.trim()) {
      toast.error('Please fill in both title and description.');
      return;
    }

    setSubmitting(true);
    try {
      let screenshotUrl = null;
      if (file) {
        const presign = await issueAPI.requestScreenshotUpload({
          contentType: file.type,
          contentLength: file.size,
        });
        const putRes = await issueAPI.uploadScreenshotToS3({
          uploadUrl: presign.data.uploadUrl,
          file,
        });
        if (!putRes.ok) {
          throw new Error(`Screenshot upload failed (${putRes.status})`);
        }
        screenshotUrl = presign.data.publicUrl;
      }

      const payload = {
        type,
        severity,
        title: title.trim(),
        description: description.trim(),
        context: buildContext(auth),
        screenshotUrl,
      };
      if (includeDiagnostics) {
        payload.diagnostics = {
          ...getDiagnosticsSnapshot(),
          breadcrumbs: getBreadcrumbsSnapshot(),
        };
      }

      const res = await issueAPI.submit(payload);
      setCreatedUrl(res.data.url);
      toast.success(`Issue #${res.data.number} created.`);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to submit issue.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto" data-issue-reporter="modal">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                data-issue-reporter="panel"
                className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-soft-xl transition sm:my-8 sm:w-full sm:max-w-xl"
              >
                <div className="bg-white">
                  <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Report an issue
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-500">
                        Sends a GitHub issue with your context attached.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      onClick={handleClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="size-6" aria-hidden="true" />
                    </button>
                  </div>

                  {createdUrl ? (
                    <div className="px-6 py-8 text-center">
                      <p className="text-sm text-gray-600 mb-4">Thanks. Your report was filed:</p>
                      <a
                        href={createdUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-medium text-wrg-blue-500 hover:text-wrg-blue-600"
                      >
                        {createdUrl}
                        <ArrowTopRightOnSquareIcon className="size-4" />
                      </a>
                      <div className="mt-6">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="rounded-md bg-wrg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-wrg-blue-600"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={submit} className="px-6 py-4 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label htmlFor="issue-type" className="block text-sm font-medium text-gray-700 mb-1">
                            Type
                          </label>
                          <select
                            id="issue-type"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                          >
                            {TYPES.map((t) => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="issue-severity" className="block text-sm font-medium text-gray-700 mb-1">
                            Severity
                          </label>
                          <select
                            id="issue-severity"
                            value={severity}
                            onChange={(e) => setSeverity(e.target.value)}
                            className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                          >
                            {SEVERITIES.map((s) => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="issue-title" className="block text-sm font-medium text-gray-700 mb-1">
                          Title
                        </label>
                        <input
                          id="issue-title"
                          type="text"
                          required
                          maxLength={200}
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="Short summary"
                          className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="issue-description" className="block text-sm font-medium text-gray-700 mb-1">
                          What happened?
                        </label>
                        <textarea
                          id="issue-description"
                          rows={6}
                          required
                          maxLength={8000}
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Steps to reproduce, expected vs actual behavior, anything else useful."
                          className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                        />
                      </div>

                      <div>
                        <p className="block text-sm font-medium text-gray-700 mb-1">
                          Screenshot <span className="text-gray-400 font-normal">(optional)</span>
                        </p>
                        {thumbnail ? (
                          <div className="flex items-start gap-3">
                            <img
                              src={thumbnail}
                              alt="Attached screenshot"
                              className="h-24 w-32 object-cover rounded-md border border-gray-200"
                            />
                            <div className="flex flex-col gap-2">
                              <button
                                type="button"
                                onClick={onEditScreenshot}
                                disabled={capturing || submitting}
                                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                <PencilSquareIcon className="size-4 text-gray-400" />
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => replaceFile(null)}
                                disabled={capturing || submitting}
                                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                <TrashIcon className="size-4 text-gray-400" />
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={onCaptureScreen}
                              disabled={capturing || submitting}
                              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              <CameraIcon className="size-5 text-gray-400" />
                              {capturing ? 'Capturing…' : 'Capture current screen'}
                            </button>
                            <label className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                              <PhotoIcon className="size-5 text-gray-400" />
                              Upload file
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                className="sr-only"
                                onChange={onFileChange}
                              />
                            </label>
                          </div>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          PNG, JPEG, or WebP up to 5 MB. Captures use html2canvas and may render embedded maps as a blank rectangle.
                        </p>
                      </div>

                      <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={includeDiagnostics}
                          onChange={(e) => setIncludeDiagnostics(e.target.checked)}
                          className="mt-0.5 rounded border-gray-300 text-wrg-blue-500 focus:ring-wrg-blue-500"
                        />
                        <span>
                          Include console + network logs and recent actions
                          <span className="block text-xs text-gray-500">
                            Helpful for debugging. Untick if this page contains sensitive data.
                          </span>
                        </span>
                      </label>

                      <p className="text-xs text-gray-500">
                        Your name, email, current page URL, browser, and app version are included automatically.
                      </p>

                      <div className="flex justify-end gap-3 pt-2">
                        <button
                          type="button"
                          onClick={handleClose}
                          disabled={submitting}
                          className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={submitting || capturing || !auth.user}
                          className="inline-flex items-center rounded-md bg-wrg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-wrg-blue-600 disabled:opacity-50"
                        >
                          {submitting ? 'Submitting…' : 'Submit'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default IssueReporterModal;
